import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { useMemo, useState } from 'react';
import { useLazyGetInvoicedWorkByEmployeeAndMonthReportQuery } from 'store/api/reportingTrends';
import { InvoicedWorkByEmployeeAndMonthRow, InvoicedWorkTotals } from 'types/reporting/trends';
import { t } from 'utils/getTranslations';
import { ChartReportView } from '../charts/ChartReportView';
import { EmployeePeriodCharts } from '../charts/EmployeePeriodCharts';
import {
    currentYearSearchParams,
    EMPLOYEE_NAME_SEARCH,
    employeeTrendFilters,
    yearFilter,
} from '../charts/trendFilters';
import { formatReportNumber, pivotToneRule, ReportTone } from '../reportCells';
import { ReportEntry } from '../reportCatalog';
import { inBaseCurrency, useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { quickFiltersFrom } from '../reportFilters';

const TONE: ReportTone = 'green';

type Row = InvoicedWorkByEmployeeAndMonthRow;

const REPORT_KEY = 'invoiced-work-by-employee-and-month';

function invoicedAmount(row: Row): number | null {
    return row.invoicedAmount;
}

function buildFilterConfig(today: Date): FilterConfig {
    const filters = [yearFilter(today), ...employeeTrendFilters()];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        quickFilters: quickFiltersFrom(filters, {
            year: 'reporting.filter.year',
            isActive: 'reporting.filter.is-active',
        }),
        searchBarSelectors: EMPLOYEE_NAME_SEARCH,
        filters,
    };
}

function renderCharts(rows: Row[], totals: InvoicedWorkTotals, currency: string | null) {
    return (
        <EmployeePeriodCharts
            tone={TONE}
            rows={rows}
            period="month"
            value={invoicedAmount}
            measure={inBaseCurrency(t('reporting.column.invoiced-amount'), currency)}
            chartTitle={t(`reporting.${REPORT_KEY}.chart`)}
            tableTitle={t(`reporting.${REPORT_KEY}.table`)}
            total={t('reporting.chart.total-amount', { value: formatReportNumber(totals.invoicedAmount) })}
        />
    );
}

export function InvoicedWorkByEmployeeAndMonthReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetInvoicedWorkByEmployeeAndMonthReportQuery();
    const [today] = useState(() => new Date());
    const [initialSearchParams] = useState(() => currentYearSearchParams(today));
    const filterConfig = useMemo(() => buildFilterConfig(today), [today]);
    const currency = useReportBaseCurrency();
    return (
        <ReportLayout
            report={report}
            toneLegend={[
                { label: inBaseCurrency(t('reporting.column.invoiced-amount'), currency), rule: pivotToneRule(TONE) },
            ]}
        >
            <ChartReportView<Row, InvoicedWorkTotals>
                analyticsSurface={`report-${REPORT_KEY}`}
                data={data}
                isFetching={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                initialSearchParams={initialSearchParams}
            >
                {(rows, totals) => renderCharts(rows, totals, currency)}
            </ChartReportView>
        </ReportLayout>
    );
}
